import {VoteList, useGetAnalysisDetail} from '@domain/rcfa'
import Add from '@mui/icons-material/Add'
import {Box, Grid, IconButton, Skeleton, Stack, Typography} from '@mui/material'
import React, {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import ReactQuill from 'react-quill'

import {FEATURE_NAMESPACE} from '../../../localization'
import {NodeAttachment} from '../../common/Attachment'
import {RcfaCanvasNode} from '../canvas/canvas.types'
import {NodeComments, AddCommentForm} from '../comments'
import {NodeVotes} from '../node-votes'
import {NodeTasks} from '../tasks/NodeTasks'

import {MarkRootCause} from './MarkRootCause'
import {NodeModalActiveView, NodeDetailType, NodePermissions} from './nodeDetail.types'
import {RootNodeDetails} from './RootNodeDetails'

type NodeVotingSectionProps = {
  analysisId: string
  nodeId: number
  rootCauseType: NodeDetailType['rootCauseType']
  voteList: VoteList
  permissions: NodePermissions
}

export const NodeVotingSection = ({
  analysisId,
  nodeId,
  rootCauseType,
  voteList,
  permissions
}: NodeVotingSectionProps) => (
  <Grid display="flex" sx={{height: '40px'}} gap={1}>
    <MarkRootCause analysisId={analysisId} nodeId={nodeId} rootCauseType={rootCauseType} />
    <NodeVotes
      voteList={voteList}
      analysisId={analysisId}
      nodeId={nodeId}
      permissions={permissions}
    />
  </Grid>
)

type NodeAttachmentsSectionProps = {
  isLoading: boolean
  attachments: NodeDetailType['attachments'] | undefined
  attachmentCount: number
  setActiveImageId: React.Dispatch<React.SetStateAction<number | undefined>>
}

export const NodeAttachmentsSection = ({
  isLoading,
  attachments,
  attachmentCount,
  setActiveImageId
}: NodeAttachmentsSectionProps) => (
  <>
    {!isLoading && !!attachments?.length && (
      <Box display="flex" flexWrap="wrap">
        {attachments.map((attachment) => (
          <NodeAttachment
            attachment={attachment}
            key={`file-${attachment.id}`}
            setActiveImageId={setActiveImageId}
          />
        ))}
      </Box>
    )}
    {isLoading && !!attachmentCount && (
      <Box gap={1} display="flex" flexWrap="wrap">
        {Array(attachmentCount)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              key={`attachment-placeholder-${index}`}
              height={100}
              width={100}
              variant="rectangular"
              sx={{flex: '0 0 auto', m: '8px'}}
            />
          ))}
      </Box>
    )}
  </>
)

type NodeCommentsSectionProps = {
  canvasNode: RcfaCanvasNode
  analysisId: string
  isCommentActive: boolean
  setIsCommentActive: React.Dispatch<React.SetStateAction<boolean>>
}

export const NodeCommentsSection = ({
  canvasNode,
  analysisId,
  isCommentActive,
  setIsCommentActive
}: NodeCommentsSectionProps) => (
  <>
    {isCommentActive && analysisId && (
      <AddCommentForm
        nodeId={canvasNode.id}
        analysisId={analysisId}
        closeForm={() => setIsCommentActive(false)}
      />
    )}
    <NodeComments nodeId={canvasNode.id} expectedCount={canvasNode.commentCount} />
  </>
)

type NodeContentSectionProps = {
  canvasNode: RcfaCanvasNode
  analysisId: string
  isLoading: boolean
  isMainNode: boolean
  data: NodeDetailType | undefined
  analysisData: ReturnType<typeof useGetAnalysisDetail>['data']
  showRootNodeDetails: boolean
  isCommentActive: boolean
  setIsCommentActive: React.Dispatch<React.SetStateAction<boolean>>
  setActiveView: React.Dispatch<React.SetStateAction<NodeModalActiveView>>
  setActiveImageId: React.Dispatch<React.SetStateAction<number | undefined>>
  openTaskDetail: (taskId: string) => void
}

export const NodeContentSection = ({
  canvasNode,
  analysisId,
  isLoading,
  isMainNode,
  data,
  analysisData,
  showRootNodeDetails,
  isCommentActive,
  setIsCommentActive,
  setActiveView,
  setActiveImageId,
  openTaskDetail
}: NodeContentSectionProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return (
    <Stack spacing={2} pr={1} sx={{maxHeight: '48vh', overflowY: 'auto'}}>
      {!isLoading && data?.votes && (
        <NodeVotingSection
          analysisId={analysisId}
          nodeId={data.id}
          rootCauseType={data.rootCauseType}
          voteList={data.votes}
          permissions={data.permissions}
        />
      )}
      {isLoading && !isMainNode && (
        <Skeleton variant="rounded" height={40} width={300} sx={{flex: '0 0 auto'}} />
      )}
      {showRootNodeDetails && analysisData && (
        <RootNodeDetails analysisData={analysisData} dueDate={analysisData.dueDate} />
      )}
      <ReactQuill value={canvasNode.description} readOnly={true} theme={'bubble'} />

      <NodeSection
        title={t('node.detail.attachments')}
        canSeeButton={!!data?.permissions?.canAddAttachment}
        onClick={() => setActiveView('edit')}
        showSkeleton={isLoading}
      >
        <NodeAttachmentsSection
          isLoading={isLoading}
          attachments={data?.attachments}
          attachmentCount={canvasNode.attachmentCount}
          setActiveImageId={setActiveImageId}
        />
      </NodeSection>

      {!isMainNode && (
        <NodeSection
          title={t('label.tasks')}
          canSeeButton={!!data?.permissions?.canCreateTask}
          onClick={() => setActiveView('createTask')}
          showSkeleton={isLoading}
        >
          <NodeTasks
            nodeId={canvasNode.id}
            onTaskClick={openTaskDetail}
            expectedCount={canvasNode.taskCount}
          />
        </NodeSection>
      )}

      {!isMainNode && (
        <NodeSection
          title={t('comments.title')}
          canSeeButton={!!data?.permissions?.canAddComment && !isCommentActive}
          onClick={() => setIsCommentActive(true)}
          showSkeleton={isLoading}
        >
          <NodeCommentsSection
            canvasNode={canvasNode}
            analysisId={analysisId}
            isCommentActive={isCommentActive}
            setIsCommentActive={setIsCommentActive}
          />
        </NodeSection>
      )}
    </Stack>
  )
}

type NodeSectionProps = {
  title: string
  canSeeButton: boolean
  onClick: () => void
  children: ReactNode
  showSkeleton?: boolean
}

const NodeSection = ({title, canSeeButton, onClick, children, showSkeleton}: NodeSectionProps) => {
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">{title}</Typography>
        {showSkeleton && (
          <Box p="5px">
            <Skeleton variant="circular" width={20} height={20} sx={{flex: '0 0 auto'}} />
          </Box>
        )}
        {!showSkeleton && canSeeButton && (
          <IconButton
            color="primary"
            onClick={() => onClick()}
            data-test-id={`create-${title.toLowerCase()}-save-button`}
          >
            <Add />
          </IconButton>
        )}
      </Stack>
      {children}
    </Stack>
  )
}
